import {
  differenceOutline,
  distance,
  nearestWallSegment,
  pointInPolygon,
  polygonArea,
  polygonPerimeter,
} from "./geom";
import { commitInteriorWall, INTERIOR_REACH_IN } from "./interiorWalls";
import {
  processStroke,
  processWallStroke,
  resample,
  unionWithRoom,
  SKETCH_ADDITION_CLOSE_THRESHOLD_IN,
  SKETCH_ADDITION_MIN_AREA_SQ_IN,
  SKETCH_ADDITION_MIN_RDP_TOLERANCE_IN,
  SKETCH_ADDITION_RESAMPLE_SPACING_IN,
  type SketchResult,
} from "./sketch";
import type { Opening, Room, Vec2, WallMount } from "./types";

/** A bump thinner than this (inches) is a sliver, not a closet or alcove.
 *  `2 * area / perimeter` of a 12×12 alcove is exactly 6. */
export const ADDITION_MIN_THICKNESS_IN = 6;

/** Stroke samples this far outside the outline count as leaving the room.
 *  Wider than an interior wall's 1" on-boundary tolerance, so a qualifying
 *  run cannot also commit as a wall. */
export const ADDITION_OUTSIDE_CLEARANCE_IN = 3;

/** How much of the open stroke, ignoring the ends, must sit outside. */
export const ADDITION_OUTSIDE_MIN_IN = 12;

export const EXTEND_OUTSIDE_MESSAGE =
  "Draw a wall inside the room, or outline an addition so it meets a wall.";

export const EXTEND_TOO_SMALL_MESSAGE =
  "Draw a longer wall, or a larger addition.";

const RETRACE = /draw across the room/i;

export type RoomSketchClass =
  | {
      ok: true;
      kind: "addition";
      /** Grown outline, ready to pass to `growOutline`. */
      points: Vec2[];
      message: string;
      warnings: string[];
      snapped: boolean;
    }
  | {
      ok: true;
      kind: "wall";
      /** Fitted open stroke, ready to pass to `commitInteriorWall`. */
      points: Vec2[];
      message: string;
      warnings: string[];
    }
  | { ok: false; message: string };

/**
 * Decide whether a stroke drawn in the unified shape tool grows the room
 * or becomes an interior wall.
 *
 * An addition has to be drawn by the stroke itself: the open path, minus
 * {@link INTERIOR_REACH_IN} at each end, must travel outside the outline and
 * the union must add a bump at least a foot square and {@link ADDITION_MIN_THICKNESS_IN}
 * thick. The polygon's closing chord is ignored, so a stroke that stays
 * inside an L cannot fill the notch. Anything else that commits as an
 * interior wall is a wall. End overshoot is clipped by the wall pipeline
 * and does not count as leaving the room.
 */
export function classifyRoomSketch(
  room: Room,
  raw: Vec2[],
  openings: Opening[],
  mounts: WallMount[],
  opts: { freeAngles?: boolean } = {}
): RoomSketchClass {
  const wallStroke = processWallStroke(raw, { freeAngles: opts.freeAngles });
  const wallCommit = wallStroke.ok
    ? commitInteriorWall(room, wallStroke.points, openings, mounts)
    : null;

  const poly = processStroke(raw, {
    freeAngles: opts.freeAngles,
    resampleSpacing: SKETCH_ADDITION_RESAMPLE_SPACING_IN,
    minRdpToleranceIn: SKETCH_ADDITION_MIN_RDP_TOLERANCE_IN,
    minAreaSqIn: SKETCH_ADDITION_MIN_AREA_SQ_IN,
    closeThresholdIn: SKETCH_ADDITION_CLOSE_THRESHOLD_IN,
  });
  const union = poly.ok ? unionWithRoom(room.points, poly.points) : null;
  const metrics = union?.ok ? bumpMetrics(union.points, room.points) : null;
  const open = openPath(raw, wallStroke);
  const outside = outsideMiddleLength(open, room.points);
  const additionOk =
    !!union?.ok &&
    !!metrics &&
    metrics.area + 1e-3 >= SKETCH_ADDITION_MIN_AREA_SQ_IN &&
    metrics.thickness + 1e-3 >= ADDITION_MIN_THICKNESS_IN &&
    outside + 1e-3 >= ADDITION_OUTSIDE_MIN_IN;

  // The outside run is measured on the middle of the stroke, so a pass here
  // is the "addition wins a tie" case. A stroke that only leaves the room
  // at its ends fails this gate and stays a wall.
  if (additionOk && union?.ok && poly.ok) {
    return {
      ok: true,
      kind: "addition",
      points: union.points,
      message: union.snapped
        ? "Snapped addition to the nearest wall"
        : "Addition merged into the room",
      warnings: poly.warnings,
      snapped: union.snapped,
    };
  }

  if (wallCommit?.ok && wallStroke.ok) {
    return {
      ok: true,
      kind: "wall",
      points: wallCommit.stroke,
      message: wallStroke.warnings[0] ?? "Snapped to a clean wall",
      warnings: wallStroke.warnings,
    };
  }

  return {
    ok: false,
    message: failureMessage(
      wallStroke,
      wallCommit && !wallCommit.ok ? wallCommit : null,
      poly,
      union && !union.ok ? union : null,
      open,
      room.points
    ),
  };
}

/** Inches of `open`, excluding {@link INTERIOR_REACH_IN} at each end, that
 *  sit more than `clearanceIn` outside `polygon`. */
export function outsideMiddleLength(
  open: Vec2[],
  polygon: Vec2[],
  clearanceIn = ADDITION_OUTSIDE_CLEARANCE_IN
): number {
  const total = pathLength(open);
  const skip = INTERIOR_REACH_IN;
  if (open.length < 2 || total <= skip * 2) return 0;
  const windowStart = skip;
  const windowEnd = total - skip;
  let outside = 0;
  let traveled = 0;
  for (let i = 1; i < open.length; i++) {
    const a = open[i - 1];
    const b = open[i];
    const segLen = distance(a, b);
    if (segLen === 0) continue;
    const segStart = traveled;
    const from = Math.max(segStart, windowStart);
    const to = Math.min(segStart + segLen, windowEnd);
    if (to > from) {
      const steps = Math.max(1, Math.ceil((to - from) / 1));
      const dt = (to - from) / steps;
      for (let s = 0; s < steps; s++) {
        const arc = from + (s + 0.5) * dt;
        const t = (arc - segStart) / segLen;
        const p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
        if (clearlyOutside(p, polygon, clearanceIn)) outside += dt;
      }
    }
    traveled += segLen;
  }
  return outside;
}

function openPath(raw: Vec2[], wall: SketchResult): Vec2[] {
  if (wall.ok) return wall.points;
  const spaced = resample(raw, SKETCH_ADDITION_RESAMPLE_SPACING_IN);
  return spaced.length >= 2 ? spaced : raw.slice();
}

function pathLength(pts: Vec2[]): number {
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += distance(pts[i - 1], pts[i]);
  return total;
}

function clearlyOutside(p: Vec2, polygon: Vec2[], clearanceIn: number): boolean {
  if (polygon.length < 3 || pointInPolygon(p, polygon)) return false;
  const wall = nearestWallSegment(p, polygon);
  return !!wall && wall.distance > clearanceIn;
}

function bumpMetrics(
  unionPoints: Vec2[],
  roomPoints: Vec2[]
): { area: number; thickness: number } | null {
  const added = differenceOutline(unionPoints, roomPoints);
  if (!added || added.length < 3) return null;
  const area = polygonArea(added);
  const perim = polygonPerimeter(added);
  if (perim <= 0) return null;
  return { area, thickness: (2 * area) / perim };
}

function isSmall(result: SketchResult): boolean {
  return !result.ok && (result.reason === "too-small" || result.reason === "too-few-points");
}

function failureMessage(
  wallStroke: SketchResult,
  wallCommit: { ok: false; message: string } | null,
  poly: SketchResult,
  union: { ok: false; reason: "disjoint" | "self-intersecting" } | null,
  open: Vec2[],
  polygon: Vec2[]
): string {
  if (wallCommit && RETRACE.test(wallCommit.message)) {
    // A shallow bump within the wall tool's 6" join snap is pulled onto the
    // outline and then reported as a retrace. The stroke itself left the
    // room, so say so instead of asking for a wall across the room.
    const left = outsideMiddleLength(open, polygon, 1);
    if (left >= 1) return EXTEND_OUTSIDE_MESSAGE;
    return wallCommit.message;
  }
  if (!poly.ok && poly.reason === "self-intersecting") return poly.message;
  if (union?.reason === "self-intersecting") {
    return "That addition crosses itself — try again.";
  }
  if (isSmall(wallStroke) && isSmall(poly)) return EXTEND_TOO_SMALL_MESSAGE;
  return EXTEND_OUTSIDE_MESSAGE;
}
